import { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Text clamped to `clampLines` with a "Ver más" / "Ver menos" toggle.
 *
 * The toggle only appears when the text is actually cut off, so short
 * descriptions don't grow a pointless link. Overflow is measured while
 * clamped (expanded, scrollHeight always equals clientHeight) and the result
 * is remembered, so the "Ver menos" link survives the expansion.
 */
export default function ExpandableText({
  text,
  className = "",
  clampLines = 2,
  testId,
}) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const ref = useRef(null);

  const measure = () => {
    const el = ref.current;
    // Only meaningful while clamped.
    if (!el || expanded) return;
    setOverflows(el.scrollHeight > el.clientHeight + 1);
  };

  // Measure before paint so the link doesn't flash in on first render.
  useLayoutEffect(measure, [text, clampLines, expanded]);

  useEffect(() => {
    // Re-check on resize: a narrower card can start cutting text that fitted.
    const onResize = () => {
      if (!expanded) measure();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  if (!text) return null;

  return (
    <div data-testid={testId}>
      <p
        ref={ref}
        className={`whitespace-pre-line ${className}`}
        style={
          expanded
            ? undefined
            : {
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: clampLines,
                overflow: "hidden",
              }
        }
      >
        {text}
      </p>
      {(overflows || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-xs text-brand hover:text-brand-hover font-medium mt-0.5"
          data-testid={testId ? `${testId}-toggle` : undefined}
        >
          {expanded ? "Ver menos" : "Ver más"}
        </button>
      )}
    </div>
  );
}
